// The intake form's values and the call they become (TZ 7.2), shared by the service page
// /calls/new and the dispatcher desk's intake panel (STAGE_4 4.4).

import type { FormInstance } from 'antd';

import { fieldErrors } from '@/api/errors';
import { ru } from '@/i18n/ru';
import { dayjs, fromApiDate, toApiDate, type Dayjs } from '@/lib/dates';

import type { CallCreate, RecentCall } from './callsApi';

export type AgeUnit = NonNullable<CallCreate['patient_age']>['unit'];
/** "54 years" or a birth date: the operator says which one the caller gave. */
export type AgeMode = 'age' | 'date';

export interface IntakeValues {
  patient_phone?: string;
  patient_name?: string;
  address?: string;
  type: CallCreate['type'];
  urgency: CallCreate['urgency'];
  reason_id?: number;
  payer_type: CallCreate['payer_type'];
  contract_id?: number | null;
  // Who handed the call over (services.referrals); the second row's field.
  referrer_id?: number | null;
  patient_id?: number | null;
  parent_call_id?: number | null;
  kind: CallCreate['kind'];
  source: CallCreate['source'];
  place_id?: number | null;
  address_details?: string;
  reason_extra?: string;
  caller_name?: string;
  caller_phone?: string;
  caller_relation?: string;
  operator_note?: string;
  dest_hospital_id?: number | null;
  dest_address?: string;
  transport_tariff_id?: number | null;
  patient_sex?: 'male' | 'female' | null;
  age_mode: AgeMode;
  age_value?: number | null;
  age_unit: AgeUnit;
  patient_birth_date?: Dayjs | null;
}

export const INTAKE_DEFAULTS: Pick<
  IntakeValues,
  'type' | 'urgency' | 'payer_type' | 'kind' | 'source' | 'age_mode' | 'age_unit'
> = {
  type: 'doctor_visit',
  urgency: 'urgent',
  payer_type: 'patient',
  kind: 'primary',
  source: 'phone',
  age_mode: 'age',
  age_unit: 'years',
};

/** How old somebody born on `birthDate` is at `now`: in years, or in months and then
 * days for a child under a year - the way the intake asks for it. */
export function ageOf(birthDate: string, now: number): { value: number; unit: AgeUnit } {
  const born = dayjs(birthDate);
  const at = dayjs(now);
  const years = at.diff(born, 'year');
  if (years >= 1) return { value: years, unit: 'years' };
  const months = at.diff(born, 'month');
  if (months >= 1) return { value: months, unit: 'months' };
  return { value: Math.max(0, at.diff(born, 'day')), unit: 'days' };
}

/** Sex and age from the patient card, for "take the patient's data": a birth date worked
 * out from an age comes back as that age, a real one as the date. */
export function cardSexAndAge(
  card: RecentCall['patient'],
  now: number = Date.now(),
): Partial<Pick<IntakeValues, 'patient_sex' | 'age_mode' | 'age_value' | 'age_unit' | 'patient_birth_date'>> {
  if (!card) return {};
  const sex = card.sex ? { patient_sex: card.sex } : {};
  if (!card.birth_date) return sex;
  if (!card.birth_date_approx) {
    return { ...sex, age_mode: 'date', patient_birth_date: fromApiDate(card.birth_date) };
  }
  const age = ageOf(card.birth_date, now);
  return { ...sex, age_mode: 'age', age_value: age.value, age_unit: age.unit };
}

function ageFields(values: IntakeValues): Pick<CallCreate, 'patient_age' | 'patient_birth_date'> {
  if (values.age_mode === 'date') {
    const date = toApiDate(values.patient_birth_date);
    return date ? { patient_birth_date: date } : {};
  }
  if (values.age_value === null || values.age_value === undefined) return {};
  return { patient_age: { value: values.age_value, unit: values.age_unit } };
}

const COORDINATE_DIGITS = 5;

export function roundCoordinate(value: number): number {
  return Number(value.toFixed(COORDINATE_DIGITS));
}

export interface IntakePoint {
  lat: number;
  lng: number;
}

/** What the form sends: empty optional fields are left out, not sent empty. */
export function buildCallBody(
  values: IntakeValues,
  startedAt: string,
  point: IntakePoint | null,
  // The line's call the call is taken from (STAGE_T T.3).
  phoneCallId?: number | null,
): CallCreate {
  return {
    ...(phoneCallId ? { phone_call_id: phoneCallId } : {}),
    type: values.type,
    urgency: values.urgency,
    source: values.source,
    kind: values.kind,
    patient_name: values.patient_name ?? '',
    address: values.address ?? '',
    reason_id: values.reason_id ?? 0,
    payer_type: values.payer_type,
    intake_started_at: startedAt,
    ...(values.patient_phone ? { patient_phone: values.patient_phone } : {}),
    ...(values.patient_id ? { patient_id: values.patient_id } : {}),
    ...(values.parent_call_id ? { parent_call_id: values.parent_call_id } : {}),
    ...(point ? { lat: point.lat, lng: point.lng } : {}),
    ...(values.place_id ? { place_id: values.place_id } : {}),
    ...(values.address_details ? { address_details: values.address_details } : {}),
    ...(values.reason_extra ? { reason_extra: values.reason_extra } : {}),
    ...(values.caller_name ? { caller_name: values.caller_name } : {}),
    ...(values.caller_phone ? { caller_phone: values.caller_phone } : {}),
    ...(values.caller_relation ? { caller_relation: values.caller_relation } : {}),
    ...(values.operator_note ? { operator_note: values.operator_note } : {}),
    ...(values.contract_id ? { contract_id: values.contract_id } : {}),
    ...(values.referrer_id ? { referrer_id: values.referrer_id } : {}),
    ...(values.dest_hospital_id ? { dest_hospital_id: values.dest_hospital_id } : {}),
    ...(values.dest_address && !values.dest_hospital_id ? { dest_address: values.dest_address } : {}),
    ...(values.transport_tariff_id ? { transport_tariff_id: values.transport_tariff_id } : {}),
    ...(values.patient_sex ? { patient_sex: values.patient_sex } : {}),
    ...ageFields(values),
  } satisfies CallCreate;
}

/** Marks the fields a 422 named; false when it named none the form could show. */
export function markFieldErrors(form: FormInstance<IntakeValues>, error: unknown): boolean {
  // The server names the field; antd types the name from the form values.
  // The age goes as one object; its number is the field that shows the error.
  const fields = fieldErrors(error).map(({ field }) =>
    field === 'patient_age' ? 'age_value' : (field as keyof IntakeValues),
  );
  if (fields.length === 0) return false;
  form.setFields(fields.map((name) => ({ name, errors: [ru.errors.invalidValue] })));
  return true;
}
